import { FormInstance } from 'antd';
import { Form } from '@common/components';
import { Store } from 'antd/lib/form/interface';
import React from 'react';

import { useMutation } from '@common/hooks';
import { ComplianceItemUploadInput } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemType } from '@onboarding/graphql';
import {
  CreateNurseCertificationMutation,
  UpdateNurseCertificationMutation,
} from 'works/legacy/graphql/nurse-certification';
import { formatToYearMonthDay } from '@common/services/datetime';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemType;
  refetchQueries: string[];
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemAddAttachmentFormCertification({
  form,
  complianceItem,
  refetchQueries,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update certification. Please try again.');

  const [updateNurseCertification, { loading: formSubmitting }] = useMutation(
    UpdateNurseCertificationMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateNurseCertification.errors) {
          addErrorsToForm(form, response.updateNurseCertification.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage(
            'success',
            'Certification has been successfully updated.'
          );
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const [createNurseCertification] = useMutation(
    CreateNurseCertificationMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.createNurseCertification.errors) {
          addErrorsToForm(form, response.createNurseCertification.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage(
            'success',
            'Certification has been successfully created.'
          );
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    if (!detailItem) {
      createNurseCertification({
        variables: {
          worksListItemId: complianceItem.id,
          worksProfileId:
            complianceItem.worksList.staffingRequestAssignment.worksProfile.id,
          certificationId: complianceItem.certification?.id,
          input: {
            expiresOn: formatToYearMonthDay(values.expiresOn),
            completedOn: formatToYearMonthDay(values.completedOn),
          },
          files: values.files ? values.files.fileList : null,
        },
      });

      return;
    }

    updateNurseCertification({
      variables: {
        id: complianceItem.id,
        certificationId: detailItem.certificationUuid,
        files: values.files?.fileList ?? [],
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
      },
    });
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      {detailItem?.name && (
        <Form.Item label="Certification">{detailItem.name}</Form.Item>
      )}

      <ComplianceItemInputExpirationDate
        expirationType={complianceItem.expirationType}
      />
      <ComplianceItemUploadInput multiple required />
    </Form>
  );
}
